import type { LinkProps } from "react-router";
import { BlurredTeaser, BlurredTeaserRow } from "~/components/BlurredTeaser";
import type { SetAction } from "~/hooks/use-action";
import type { PlanActions } from "~/routes/plans.$eventId/route";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { getActionCopy } from "./UrgencyPanel";
import { useRootLocale } from "~/root";
import { getRegionalCopy } from "~/utils/localization";

type ActivityFeedProps = {
  plan: EventFragment;
  isTicketed: boolean;
  isAttending: boolean;
  guestListExposed: boolean;
  atCapacity: boolean;
  onSetAction: SetAction<PlanActions>;
  onClick?: () => void;
  onSeeOtherPlans?: () => void;
  ticketsLink?: LinkProps["to"];
  ticketsDisabled?: boolean;
  className?: string;
};

const FAKE_ROWS: BlurredTeaserRow[] = [
  {
    key: "tessa",
    name: "Tessa L.",
    action: "joined the guest list",
    avatarUrl: "/images/fake-profiles/fake-profile-1.png",
  },
  {
    key: "daniel",
    name: "Daniel R.",
    action: "saved this plan",
    avatarUrl: "/images/fake-profiles/fake-profile-3.png",
  },
  {
    key: "maya",
    name: "Maya P.",
    action: "joined the waitlist",
    avatarUrl: "/images/fake-profiles/fake-profile-4.png",
  },
];

export function ActivityFeed({
  plan,
  isTicketed,
  isAttending,
  guestListExposed,
  atCapacity,
  onSetAction,
  onClick,
  onSeeOtherPlans,
  ticketsLink,
  ticketsDisabled = false,
  className,
}: ActivityFeedProps) {
  const locale = useRootLocale();
  const regionalCopy = getRegionalCopy(locale);

  if (isAttending) return null;

  const copy = getActionCopy({ plan, isAttending, atCapacity, locale });
  const isWaitlist = copy.mode === "waitlist";
  const seeOtherPlans = copy.mode === "see-other-plans";
  const isScheduled = copy.mode === "scheduled";
  const isRequestStatus = copy.mode === "request-status";

  const overlayDescription = isRequestStatus
    ? copy.sublabel
    : isWaitlist
      ? `Join the waitlist - we'll let you know the moment a ${regionalCopy.place} becomes available.`
      : seeOtherPlans
        ? "This one's gone - but there's plenty more on."
        : isScheduled
          ? "Tickets are going live soon."
          : isTicketed
            ? guestListExposed
              ? "Get your ticket to see plan activity."
              : "Get your ticket to see who's going, saving, and joining the waitlist."
            : "RSVP to see who's going, saving, and joining the waitlist.";

  const ctaLabel = isRequestStatus
    ? copy.ctaLabel
    : isWaitlist
      ? "Join the waitlist"
      : seeOtherPlans
        ? "See what else is on"
        : isScheduled
          ? "Get tickets"
          : isTicketed
            ? "Get tickets"
            : "RSVP";

  const ctaTo =
    !isWaitlist && !seeOtherPlans && isTicketed ? ticketsLink : undefined;

  const handleCta = () => {
    if (seeOtherPlans) {
      onSeeOtherPlans?.();
      return;
    }
    if (isWaitlist) {
      onClick?.();
      return;
    }
    onSetAction(undefined, {
      type:
        isTicketed && !guestListExposed
          ? "private-guest-list"
          : "private-activity",
      data: undefined,
    });
  };

  return (
    <BlurredTeaser
      heading="Activity"
      rows={FAKE_ROWS}
      overlayTitle="Things are happening"
      overlayDescription={overlayDescription}
      ctaLabel={ctaLabel}
      ctaTo={ctaTo}
      ctaDisabled={ticketsDisabled || copy.disabled}
      onCtaClick={handleCta}
      className={className}
    />
  );
}
